import { EStorageType, WebStorageService } from '@adaptiva/core/storage';
import { AuthenticationService, IOidcLoginRequest, IOidcLoginState, OidcService, PkceService } from '@adaptiva/data-service';
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Observable, take } from 'rxjs';
import { OidcLandingPage } from '../oidc-landing';

@Component({
	standalone: false,
	selector: 'ada-oidc-redirect',
	templateUrl: '../oidc-landing/oidc-landing-page.html',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class OidcRedirectComponent extends OidcLandingPage implements OnInit {

	protected readonly auth: AuthenticationService = inject(AuthenticationService);

	protected readonly location: Location = inject(Location);

	protected readonly pkce: PkceService = inject(PkceService);

	protected readonly storage: WebStorageService = inject(WebStorageService);

	public ngOnInit(): void {
		const _code: string = this.activatedRoute.snapshot.queryParams[OidcService.Params.CODE];
		const _state: string = this.activatedRoute.snapshot.queryParams[OidcService.Params.STATE];
		const _error: string = this.activatedRoute.snapshot.queryParams[OidcService.Params.ERROR];
		const _errorMessage: string = this.activatedRoute.snapshot.queryParams[OidcService.Params.ERROR_DESCRIPTION];

		if (_error || _errorMessage)
			return this.errorMessage.next(`${_error || 'Error'}: ${_errorMessage || 'Unexpected validation error'}`);

		if (!_code)
			return this.errorMessage.next('Unable to locate authentication code. Received: ' + _code);

		if (!_state)
			return this.errorMessage.next('Invalid authentication state. Received: ' + _state);

		if (_error || _errorMessage)
			return this.errorMessage.next(`${_error || 'Error'}: ${_errorMessage || 'Unexpected validation error'}`);

		const _path: string = this.location.path().split('?')[0];
		const _oidcState: IOidcLoginState = this.oidc.decodeOidcState(_state);
		const _cachedRequest: IOidcLoginRequest = this.storage.getRecord(this.oidc.cachedOidcRequestStorage);
		let _oidcRequest: IOidcLoginRequest;

		if (_cachedRequest && _code === _cachedRequest.authorizationCode)
			_oidcRequest = _cachedRequest;
		else {
			_oidcRequest = {
				authorizationCode: _code,
				codeVerifier: this.pkce.fetchCodeVerifier(),
				redirectUri: this.oidc.getRedirectUrl(),
				oidcProviderId: _oidcState.providerId,
			};
		}

		if (_oidcState.redirectTo && _oidcState.redirectTo !== _path) {
			const _url: URL = new URL(_oidcState.redirectTo, location.origin);

			_url.searchParams.set(OidcService.Params.CODE, _code);
			_url.searchParams.set(OidcService.Params.STATE, _state);

			this.storage.setRecord(this.oidc.cachedOidcRequestStorage, _oidcRequest);
			this.router.navigateByUrl(`${_oidcState.redirectTo}?${_url.searchParams.toString()}`);

			return;
		}

		this.executeLoginTask(_oidcRequest).pipe(take(1)).subscribe({
			next: () => {
				this.router.navigateByUrl(_oidcState.requestedUrl || '/');
			},
			error: (e: Error) => {
				const _message: string = this.logger.extractErrorMessage(e);

				this.errorMessage.next(_message);
				this.logger.error('OidcRedirectComponent.ngOnInit', _message)();
			}
		});
	}

	protected getRedirectUrl(): string {
		return this.oidc.getRedirectUrl();
	}

	protected executeLoginTask(_loginRequest: IOidcLoginRequest): Observable<void> {
		return this.auth.oidcLogin(_loginRequest);
	}

}
